import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import useGetMutationFn from '../use-get-mutation-fn';
import { assetKeys } from './assets.query';

/** One signal definition with its coverage (GET /api/admin/assets/signals). */
export type SignalDefinitionRow = {
  sdid: number;
  signal_key: string;
  category: string;
  processor: string;
  version: string;
  active: number;
  paid: boolean;
  coverage: number;
  overridden: number;
  last_computed: string | null;
  labels: Record<string, number>;
  /** Ten buckets of ten (0–9 … 90–100); null for descriptive signals. */
  histogram: number[] | null;
};

export type SignalProcessor = {
  processor: string;
  keys: string[];
  active_keys: string[];
  paid: boolean;
  runnable: boolean;
  version: string;
};

export type SignalsOverview = {
  eligible_images: number;
  processors: SignalProcessor[];
  definitions: SignalDefinitionRow[];
  categories: { scid: number; name_system: string; name: string; threshold_pass: number | null; agg_strategy: string; active: number }[];
};

export type AssetSignalRow = {
  signal_key: string;
  category?: string;
  value: number | null;
  label: string | null;
  content: string | null;
  content_json: unknown;
  confidence: number | null;
  reason_code: string | null;
  source: string | null;
  processor_version: string;
  override: { value: number | null; label: string | null; by: number | null; reason: string | null; at: string | null } | null;
  effective_value: number | null;
  effective_label: string | null;
  date_modified: string | null;
  history_count: number;
};

export type AssetSignals = { asid: number; signals: AssetSignalRow[]; processors: SignalProcessor[] };

export type SignalHistoryRow = {
  signal_key: string;
  value: number | null;
  label: string | null;
  content: string | null;
  processor_version: string | null;
  source: string | null;
  shadow: number;
  job_id: number | null;
  date_added: string | null;
};

const body = (raw: any) => (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw ?? {});

export const signalKeys = {
  overview: ['asset-signals', 'overview'] as const,
  asset: (asid: number) => ['asset-signals', 'asset', asid] as const,
  history: (asid: number, key: string) => ['asset-signals', 'history', asid, key] as const,
};

const useGet = <T,>(message: string) => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (url: string): Promise<T> => body(await request(url)) as T, message);
};

export const useSignalsOverviewQuery = () => {
  const get = useGet<SignalsOverview>('Could not load signals');
  return useQuery({ queryKey: signalKeys.overview, queryFn: () => get('/api/admin/assets/signals'), staleTime: 60_000 });
};

export const useAssetSignalsQuery = (asid: number | undefined) => {
  const get = useGet<AssetSignals>('Could not load signals for this asset');
  return useQuery({
    queryKey: signalKeys.asset(asid ?? 0),
    queryFn: () => get(`/api/admin/assets/${asid}/signals`),
    enabled: asid != null,
    placeholderData: keepPreviousData,
  });
};

export const useSignalHistoryQuery = (asid: number, key: string, enabled: boolean) => {
  const get = useGet<{ history: SignalHistoryRow[] }>('Could not load signal history');
  return useQuery({
    queryKey: signalKeys.history(asid, key),
    queryFn: () => get(`/api/admin/assets/${asid}/signals/history?signal_key=${encodeURIComponent(key)}&limit=50`),
    enabled,
  });
};

const useInvalidateSignals = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['asset-signals'] });
    qc.invalidateQueries({ queryKey: assetKeys.all });
  };
};

export type RunSignalsPayload = { asid: number; processors?: string[]; dry_run?: boolean; confirm_paid?: boolean };

export const useRunSignalsMutation = () => {
  const invalidate = useInvalidateSignals();
  const fn = useGetMutationFn<RunSignalsPayload, any>({
    defaultErrorMessage: 'Could not queue the analysis',
    endpoint: ({ asid }) => `/api/admin/assets/${asid}/signals/run`,
    method: 'POST',
  });
  return useMutation({ mutationFn: async (p: RunSignalsPayload) => body(await fn(p)), onSuccess: invalidate });
};

export type OverridePayload = { asid: number; key: string; value?: number | null; label?: string | null; reason: string; clear?: boolean };

export const useOverrideSignalMutation = () => {
  const invalidate = useInvalidateSignals();
  const set = useGetMutationFn<OverridePayload, any>({
    defaultErrorMessage: 'Could not save the override',
    endpoint: ({ asid, key }) => `/api/admin/assets/${asid}/signals/${encodeURIComponent(key)}/override`,
    method: 'PUT',
  });
  const clear = useGetMutationFn<OverridePayload, any>({
    defaultErrorMessage: 'Could not clear the override',
    endpoint: ({ asid, key }) => `/api/admin/assets/${asid}/signals/${encodeURIComponent(key)}/override`,
    method: 'DELETE',
  });
  return useMutation({ mutationFn: (p: OverridePayload) => (p.clear ? clear(p) : set(p)), onSuccess: invalidate });
};

export const useToggleSignalDefinitionMutation = () => {
  const invalidate = useInvalidateSignals();
  const fn = useGetMutationFn<{ sdid: number; active: boolean; reason: string }, any>({
    defaultErrorMessage: 'Could not change the signal',
    endpoint: ({ sdid }) => `/api/admin/assets/signals/definitions/${sdid}`,
    method: 'PUT',
  });
  return useMutation({ mutationFn: fn, onSuccess: invalidate });
};

/** "nudity" -> "Nudity", "child_unsafe_visual" -> "Child unsafe visual". */
export const signalName = (key: string) => {
  const s = key.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const processorName = (p: string) =>
  ({
    'task:asset-vision': 'Vision (AI)',
    'task:asset-local-metrics': 'Local metrics',
    'task:asset-document': 'Document labelling',
    'task:asset-vision-relevance': 'Relevance (AI)',
  })[p] ?? p.replace(/^task:/, '');

/** Labels that mean "a person should look at this". */
export const FLAG_LABELS = new Set(['unsafe', 'review', 'watermarked', 'contact_info', 'stock', 'duplicate', 'blurry', 'low_resolution']);

// ---- Versions (phase V) -----------------------------------------------------

export type SignalVersion = {
  svid: number;
  processor: string;
  label: string;
  code_version: number | null;
  inputs: Record<string, unknown> | null;
  status: 'candidate' | 'active' | 'previous' | 'retired';
  notes: string | null;
  running: boolean;
  first_seen: string | null;
  promoted_at: string | null;
  current_assets: number;
  history_assets: number;
  shadow_assets: number;
};

export type VersionCompare = {
  processor: string;
  a: string;
  b: string;
  assets_compared: number;
  keys: Record<string, { compared: number; agreement: number | null; flips: Record<string, number>; delta_histogram: number[] }>;
  flipped_sample: { asid: number; signal_key: string; a: { value: number | null; label: string | null }; b: { value: number | null; label: string | null } }[];
};

export const useSignalVersionsQuery = () => {
  const get = useGet<{ versions: SignalVersion[] }>('Could not load signal versions');
  return useQuery({ queryKey: ['asset-signals', 'versions'], queryFn: () => get('/api/admin/assets/signals/versions') });
};

export const useVersionCompareQuery = (processor: string, a: string | null, b: string | null) => {
  const get = useGet<VersionCompare>('Could not compare versions');
  return useQuery({
    queryKey: ['asset-signals', 'compare', processor, a, b],
    queryFn: () => get(`/api/admin/assets/signals/versions/compare?processor=${encodeURIComponent(processor)}&a=${encodeURIComponent(a!)}&b=${encodeURIComponent(b!)}`),
    enabled: !!(a && b),
  });
};

export const useVersionChangeMutation = () => {
  const invalidate = useInvalidateSignals();
  const fn = useGetMutationFn<{ svid: number; verb: 'promote' | 'retire' | 'rollback'; reason: string }, any>({
    defaultErrorMessage: 'Could not change the version',
    endpoint: ({ svid, verb }) => `/api/admin/assets/signals/versions/${svid}/${verb}`,
    method: 'POST',
  });
  return useMutation({ mutationFn: fn, onSuccess: invalidate });
};
